import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import ServiceCard from '../../components/base/ServiceCard';
import TestimonialCard from '../../components/base/TestimonialCard';

export default function HomePage() {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'VaciadoPro Barcelona - Vaciado de Pisos Gratis en Barcelona';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Especialistas en vaciado de pisos en Barcelona. Servicio gratis si los enseres cubren el coste. Presupuesto inmediato por WhatsApp. ¡Llama ahora!');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'vaciado pisos Barcelona, vaciado gratis Barcelona, recogida muebles Barcelona, síndrome Diógenes Barcelona, vaciado locales Barcelona');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "VaciadoPro Barcelona",
      "description": "Especialistas en vaciado de pisos en Barcelona. Servicio gratis si los enseres cubren el coste.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "telephone": "+34 123 456 789",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Barcelona",
        "addressRegion": "Cataluña",
        "addressCountry": "ES"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 41.3851,
        "longitude": 2.1734
      },
      "openingHours": "Mo-Su 08:00-20:00",
      "serviceArea": {
        "@type": "City",
        "name": "Barcelona"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Servicios de Vaciado",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Pisos",
              "description": "Vaciado completo de pisos y locales"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Recogida de Muebles",
              "description": "Recogida y transporte de muebles y enseres"
            }
          }
        ]
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Vaciado de Pisos',
      description: 'Vaciamos pisos completos de forma rápida y profesional. Evaluamos el valor de los enseres para ofrecerte el mejor precio.',
      image: 'https://public.readdy.ai/ai/img_res/3cc69ca31f471684375d6edef5b536fe.jpg',
      features: [
        'Servicio en 24-48 horas',
        'Valoración gratuita',
        'Equipo profesional',
        'Totalmente asegurado'
      ]
    },
    {
      icon: 'ri-building-line',
      title: 'Vaciado de Locales y Oficinas',
      description: 'Especialistas en vaciado de espacios comerciales. Nos adaptamos a tus horarios y necesidades empresariales.',
      image: 'https://readdy.ai/api/search-image?query=Commercial%20office%20space%20clearing%20Barcelona%20professional%20workers%20removing%20office%20furniture%20modern%20business%20environment%20clean%20organized%20efficient&width=400&height=300&seq=vaciado-oficinas-home&orientation=landscape',
      features: [
        'Horarios flexibles',
        'Gestión de equipos',
        'Servicio nocturno disponible',
        'Certificados incluidos'
      ]
    },
    {
      icon: 'ri-truck-line',
      title: 'Recogida de Muebles',
      description: 'Recogemos muebles y enseres específicos. Servicio puntual y cuidadoso con tus pertenencias.',
      image: 'https://readdy.ai/api/search-image?query=Furniture%20collection%20service%20Barcelona%20professional%20workers%20loading%20large%20items%20into%20truck%20efficient%20organized%20team%20modern%20van%20clean%20background&width=400&height=300&seq=recogida-muebles-home&orientation=landscape',
      features: [
        'Recogida a domicilio',
        'Sin límite de volumen',
        'Carga incluida',
        'Mismo día disponible'
      ]
    },
    {
      icon: 'ri-heart-line',
      title: 'Casos Especiales',
      description: 'Tratamos casos delicados como Síndrome de Diógenes con total discreción y profesionalidad.',
      image: 'https://readdy.ai/api/search-image?query=Sensitive%20professional%20cleaning%20service%20Barcelona%20discrete%20workers%20careful%20approach%20protective%20equipment%20respectful%20team%20gentle%20lighting%20clean%20background&width=400&height=300&seq=casos-especiales-home&orientation=landscape',
      features: [
        'Máxima discreción',
        'Equipo especializado',
        'Protocolos de seguridad',
        'Coordinación con servicios sociales'
      ]
    }
  ];

  const testimonials = [
    {
      name: 'María P.',
      location: 'Eixample',
      rating: 5,
      comment: 'Excelente servicio. Vinieron en 24h como prometieron y el vaciado fue gratuito. Muy profesionales.'
    },
    {
      name: 'Carlos R.',
      location: 'Gràcia',
      rating: 5,
      comment: 'Rápidos, eficientes y honestos. Me ayudaron mucho en un momento difícil. Los recomiendo 100%.'
    },
    {
      name: 'Ana M.',
      location: 'Sants-Montjuïc',
      rating: 5,
      comment: 'Servicio impecable. Valoraron correctamente los muebles y el precio final fue muy justo.'
    }
  ];

  const processSteps = [
    {
      number: '1',
      title: 'Contacto y Valoración',
      description: 'Envíanos fotos/vídeo por WhatsApp para una valoración inicial gratuita.'
    },
    {
      number: '2',
      title: 'Presupuesto',
      description: 'Si los enseres son reutilizables, el servicio es GRATIS. Si no, te damos el precio más ajustado.'
    },
    {
      number: '3',
      title: 'Recogida y Limpieza',
      description: 'Acudimos el día pactado. Nos encargamos de todo el proceso de principio a fin.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20junk%20removal%20team%20working%20in%20a%20clean%20modern%20apartment%20in%20Barcelona%2C%20workers%20in%20uniform%20clearing%20furniture%20and%20boxes%2C%20bright%20natural%20lighting%2C%20clean%20organized%20space%2C%20professional%20service%20atmosphere%2C%20modern%20Spanish%20interior%20design&width=1920&height=1080&seq=hero-vaciado&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4 w-full">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Vaciado de pisos <span className="text-green-400">GRATIS</span> en Barcelona
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed">
              Servicio rápido y profesional. Valoramos tus enseres y si cubren el coste, 
              el vaciado es gratuito. ¡Pide presupuesto por WhatsApp!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                href="https://wa.me/34690220944" 
                variant="primary" 
                size="lg"
                className="flex items-center gap-3"
                target="_blank"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Pedir Presupuesto por WhatsApp
              </Button>
              <Button 
                href="/servicios" 
                variant="outline" 
                size="lg"
                className="bg-white/10 border-white text-white hover:bg-white hover:text-gray-800"
              >
                Ver Nuestros Servicios
              </Button>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-yellow-400">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <span className="text-white ml-2">4.9/5 en Google (basado en +100 reseñas)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Una solución completa para todo tipo de vaciados
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ofrecemos servicios profesionales de vaciado en Barcelona con la garantía de un trabajo bien hecho
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="relative">
                <ServiceCard
                  title={service.title}
                  description={service.description}
                  image={service.image}
                  features={service.features}
                />
                <div className="mt-4 text-center">
                  <Button 
                    href="/servicios" 
                    variant="outline" 
                    size="sm"
                    className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
                  >
                    Más Información
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Nuestro proceso: fácil y transparente
            </h2>
            <p className="text-xl text-gray-600">
              En solo 3 pasos sencillos resolvemos tu problema de vaciado
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Nuestro trabajo habla por nosotros
            </h2>
            <p className="text-xl text-gray-600">
              Transformaciones reales de nuestros clientes en Barcelona
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-100 rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Cluttered%20messy%20apartment%20room%20before%20junk%20removal%20service%2C%20old%20furniture%2C%20boxes%2C%20personal%20belongings%20scattered%20around%2C%20realistic%20interior%2C%20Barcelona%20apartment%20style%2C%20natural%20lighting&width=600&height=400&seq=before-1&orientation=landscape"
                alt="Antes del vaciado - Piso desordenado"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Antes</h3>
                <p className="text-gray-600">Piso en Eixample con acumulación de enseres</p>
              </div>
            </div>
            
            <div className="bg-gray-100 rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Clean%20empty%20apartment%20room%20after%20professional%20junk%20removal%20service%2C%20spotless%20floors%2C%20bright%20natural%20light%2C%20modern%20Barcelona%20apartment%20interior%2C%20minimalist%20clean%20space%2C%20professional%20cleaning%20result&width=600&height=400&seq=after-1&orientation=landscape"
                alt="Después del vaciado - Piso limpio"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Después</h3>
                <p className="text-gray-600">Mismo piso completamente vaciado y limpio</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Clientes satisfechos en Barcelona
            </h2>
            <p className="text-xl text-gray-600">
              La confianza de nuestros clientes es nuestro mejor aval
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                name={testimonial.name}
                location={testimonial.location}
                rating={testimonial.rating}
                comment={testimonial.comment}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Damos servicio en Barcelona ciudad y alrededores
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Operamos en todos los distritos de Barcelona (Gràcia, Sants-Montjuïc, Eixample, Sant Martí, 
              Ciutat Vella, Les Corts, Nou Barris, Sant Andreu, Sarrià-Sant Gervasi, Horta-Guinardó) 
              y municipios cercanos como L'Hospitalet de Llobregat, Badalona, Santa Coloma de Gramenet, 
              Cornellà de Llobregat y Esplugues de Llobregat.
            </p>
          </div>
          
          <div className="bg-green-50 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-map-pin-line text-white text-2xl"></i>
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              Cobertura total en el área metropolitana
            </h3>
            <p className="text-gray-600 mb-6">
              Servicio rápido de 24 horas en toda Barcelona y área metropolitana. 
              <a href="/empresa" className="text-green-600 hover:text-green-700 font-medium ml-1">
                Conoce más sobre nuestra empresa
              </a> y nuestro compromiso con el servicio de calidad.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button href="https://wa.me/34690220944" variant="primary" target="_blank">
                Consultar Disponibilidad en tu Zona
              </Button>
              <Button href="/contacto" variant="outline" className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white">
                Más Formas de Contacto
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            ¿Listo para vaciar tu piso?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            No esperes más. Contacta ahora y recibe tu presupuesto gratuito en menos de 2 horas. 
            Nuestro equipo está listo para ayudarte. Si tienes dudas, consulta nuestras 
            <a href="/faq" className="text-green-200 hover:text-white font-medium ml-1 mr-1">
              preguntas frecuentes
            </a>
            o conoce más sobre 
            <a href="/empresa" className="text-green-200 hover:text-white font-medium ml-1">
              nuestra empresa
            </a>.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              href="https://wa.me/34690220944?text=Hola, quiero mi presupuesto GRATIS para vaciado de piso en Barcelona" 
              variant="primary" 
              size="lg"
              className="flex items-center gap-3"
              target="_blank"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Presupuesto GRATIS
            </Button>
            <Button 
              href="tel:+34690220944" 
              variant="secondary" 
              size="lg"
              className="flex items-center gap-3 !bg-white !text-green-600 hover:!bg-gray-100 border-2 border-green-600"
            >
              <i className="ri-phone-line text-xl"></i>
              Llamar Ahora
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
